import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    API_URLS,
    obtenerProductos,
    obtenerUsuarios
} from '../../Services/servicioURL';

interface Sucursal {
    id: string;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

interface Usuario {
    id: number;
    nombre: string;
}

interface InventarioItem {
    cve_producto: string;
    descripcion: string;
    modelo: string;
}

interface MovimientoTotal {
    folio: string;
    fecha: string;
    tipo: 'entrada' | 'salida' | 'mantenimiento';
    observaciones?: string;
    referencia?: string;
    sucursal_destino?: string;
    departamento_destino?: string;
    usuario?: string;
    tecnico?: string;
    producto_principal?: string;
    total_productos: number;
    total_cantidad: number;
    detalles?: any[];
}

const Totales: React.FC = () => {
    const [tipoMovimiento, setTipoMovimiento] = useState<'entrada' | 'salida' | 'mantenimiento'>('entrada');
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState<Sucursal | null>(null);
    const [fechaInicio, setFechaInicio] = useState<string>('');
    const [fechaFin, setFechaFin] = useState<string>('');
    const [movimientos, setMovimientos] = useState<MovimientoTotal[]>([]);
    const [movimientosFiltrados, setMovimientosFiltrados] = useState<MovimientoTotal[]>([]);
    const [sucursalesCatalogo, setSucursalesCatalogo] = useState<Sucursal[]>([]);
    const [departamentosCatalogo, setDepartamentosCatalogo] = useState<Departamento[]>([]);
    const [usuariosCatalogo, setUsuariosCatalogo] = useState<Usuario[]>([]);
    const [productosCatalogo, setProductosCatalogo] = useState<InventarioItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [catalogosLoaded, setCatalogosLoaded] = useState<boolean>(false);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const [selectedMovimiento, setSelectedMovimiento] = useState<MovimientoTotal | null>(null);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const toast = useRef<Toast>(null);

    const tiposMovimiento = [
        { label: '📈 Entradas', value: 'entrada' },
        { label: '📉 Salidas', value: 'salida' },
        { label: '🔧 Mantenimientos', value: 'mantenimiento' }
    ];

    useEffect(() => {
        const fetchCatalogos = async () => {
            try {
                const [dataProductos, dataUsuarios, dataSucursales, dataDepartamentos] = await Promise.all([
                    obtenerProductos(API_URLS.productos.obtener),
                    obtenerUsuarios(API_URLS.usuarios.obtener),
                    fetch(API_URLS.sucursales.obtener).then(res => res.json()),
                    fetch(API_URLS.departamentos.obtener).then(res => res.json())
                ]);
                
                const inventarioData = dataProductos.map((producto: any) => ({
                    cve_producto: producto.cve,
                    descripcion: producto.descripcion,
                    modelo: producto.modelo
                }));
                setProductosCatalogo(inventarioData);
                
                const usuariosData = dataUsuarios.map((usuario: any) => ({
                    id: usuario.id,
                    nombre: usuario.nombre
                }));
                setUsuariosCatalogo(usuariosData);
                
                setSucursalesCatalogo(dataSucursales.map((s: any) => ({
                    id: String(s.id),
                    nombre: s.nombre
                })));

                setDepartamentosCatalogo(dataDepartamentos.map((d: any) => ({
                    cve: d.cve,
                    nombre: d.nombre
                })));
                
                setCatalogosLoaded(true);
                
            } catch (error) {
                console.error('Error al cargar catálogos:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar los catálogos',
                    life: 3000
                });
            }
        };
        fetchCatalogos();
    }, []);

    // Efecto para buscar automáticamente cuando se cargan los catálogos o cambia el tipo de movimiento
    useEffect(() => {
        if (catalogosLoaded) {
            buscarMovimientos();
        }
    }, [catalogosLoaded, tipoMovimiento]);

    // Función corregida para formatear fechas sin problemas de zona horaria
    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        
        try {
            // Si la fecha viene en formato ISO (YYYY-MM-DD), la parseamos directamente
            if (dateString.includes('-') && dateString.length === 10) {
                const [year, month, day] = dateString.split('-');
                return `${day}/${month}/${year}`;
            }
            
            // Si viene en otro formato, intentamos parsearlo
            const date = new Date(dateString + 'T00:00:00'); // Agregamos tiempo para evitar problemas de zona horaria
            if (isNaN(date.getTime())) return '-';
            
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC'
            });
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return '-';
        }
    };

    // Función para comparar fechas sin problemas de zona horaria
    const compareDates = (dateString1: string, dateString2: string, comparison: 'gte' | 'lte'): boolean => {
        try {
            // Convertir ambas fechas a formato YYYY-MM-DD si no lo están ya
            const normalizeDate = (dateStr: string): string => {
                if (dateStr.includes('-') && dateStr.length === 10) {
                    return dateStr;
                }
                const date = new Date(dateStr);
                return date.toISOString().split('T')[0];
            };

            const date1 = normalizeDate(dateString1);
            const date2 = normalizeDate(dateString2);

            if (comparison === 'gte') {
                return date1 >= date2;
            } else {
                return date1 <= date2;
            }
        } catch (error) {
            console.error('Error al comparar fechas:', error);
            return false;
        }
    };

    const getDescripcionProducto = (cve_producto: string): string => {
        const producto = productosCatalogo.find(p => p.cve_producto === cve_producto);
        return producto ? producto.descripcion : 'Producto no encontrado';
    };

    const getNombreTecnico = (id_tecnico: number): string => {
        const tecnico = usuariosCatalogo.find(u => u.id === id_tecnico);
        return tecnico ? tecnico.nombre : 'Técnico no encontrado';
    };

    const getNombreSucursal = (id: string | number): string => {
        const suc = sucursalesCatalogo.find(s => s.id === String(id));
        return suc ? suc.nombre : (id ? String(id) : '-');
    };

    const getNombreDepartamento = (cve: string): string => {
        const dept = departamentosCatalogo.find(d => d.cve === cve);
        return dept ? dept.nombre : (cve || '-');
    };

    const buscarMovimientos = async () => {
        setLoading(true);
        try {
            let url = '';
            switch (tipoMovimiento) {
                case 'entrada':
                    url = API_URLS.entradas.obtener;
                    break;
                case 'salida':
                    url = API_URLS.salidas.obtener;
                    break;
                case 'mantenimiento':
                    url = API_URLS.mantenimientos.obtener;
                    break;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('Error al obtener movimientos');
            
            const data = await response.json();
            
            const movimientosProcesados: MovimientoTotal[] = data.map((item: any) => {
                let totalProductos = 0;
                let totalCantidad = 0;
                let sucursalDestino = '';
                let departamentoDestino = '';
                let usuario = '';
                let tecnico = '';
                let productoP = '';

                if (tipoMovimiento === 'entrada') {
                    const detalles = item.entradaDetalle || [];
                    totalProductos = detalles.length;
                    totalCantidad = detalles.reduce((sum: number, d: any) => sum + d.cantidad, 0);
                } else if (tipoMovimiento === 'salida') {
                    const detalles = item.salidaDetalle || [];
                    totalProductos = detalles.length;
                    totalCantidad = detalles.reduce((sum: number, d: any) => sum + d.cantidad, 0);
                    
                    // Obtener sucursales y departamentos únicos
                    const sucursales = Array.from(new Set(detalles.map((d: any) => getNombreSucursal(d.id_sucursal_destino))));
                    const departamentos = Array.from(new Set(detalles.map((d: any) => getNombreDepartamento(d.cve_departamento_destino))));
                    const usuarios = Array.from(new Set(detalles.map((d: any) => d.nombre_usuario).filter(Boolean)));
                    
                    sucursalDestino = sucursales.join(', ');
                    departamentoDestino = departamentos.join(', ');
                    usuario = usuarios.join(', ');
                } else {
                    const detalles = item.mantenimientoDetalle || [];
                    totalProductos = detalles.length;
                    totalCantidad = detalles.reduce((sum: number, d: any) => sum + d.cantidad, 0);
                    tecnico = getNombreTecnico(item.id_tecnico);
                    productoP = getDescripcionProducto(item.cve_producto_principal);
                }

                return {
                    folio: item.folio,
                    fecha: item.fecha,
                    tipo: tipoMovimiento,
                    observaciones: item.observaciones || '-',
                    referencia: item.referencia || '-',
                    sucursal_destino: sucursalDestino || '-',
                    departamento_destino: departamentoDestino || '-',
                    usuario: usuario || '-',
                    tecnico: tecnico || '-',
                    producto_principal: productoP || '-',
                    total_productos: totalProductos,
                    total_cantidad: totalCantidad,
                    detalles: tipoMovimiento === 'entrada' ? item.entradaDetalle : 
                             tipoMovimiento === 'salida' ? item.salidaDetalle : 
                             item.mantenimientoDetalle
                };
            });

            // Ordenar por folio
            movimientosProcesados.sort((a, b) => a.folio.localeCompare(b.folio));
            
            setMovimientos(movimientosProcesados);
            aplicarFiltros(movimientosProcesados);

            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: `Se encontraron ${movimientosProcesados.length} ${tipoMovimiento}s`,
                life: 3000
            });

        } catch (error) {
            console.error('Error al buscar movimientos:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron obtener los movimientos',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const aplicarFiltros = (movimientosBase: MovimientoTotal[] = movimientos) => {
        let filtrados = [...movimientosBase];

        // Filtro por sucursal
        if (sucursalSeleccionada && sucursalSeleccionada.nombre && tipoMovimiento === 'salida') {
            filtrados = filtrados.filter(m => 
                m.sucursal_destino && m.sucursal_destino.includes(sucursalSeleccionada.nombre)
            );
        }

        // Filtro por rango de fechas usando la función corregida
        if (fechaInicio) {
            filtrados = filtrados.filter(m => {
                return compareDates(m.fecha, fechaInicio, 'gte');
            });
        }

        if (fechaFin) {
            filtrados = filtrados.filter(m => {
                return compareDates(m.fecha, fechaFin, 'lte');
            });
        }

        setMovimientosFiltrados(filtrados);
    };

    useEffect(() => {
        aplicarFiltros();
    }, [sucursalSeleccionada, fechaInicio, fechaFin, movimientos]);

    const limpiarFiltros = () => {
        setSucursalSeleccionada(null);
        setFechaInicio('');
        setFechaFin('');
        setGlobalFilterValue('');
        setMovimientosFiltrados(movimientos);
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGlobalFilterValue(e.target.value);
    };

    const showMovimientoDetail = (movimiento: MovimientoTotal) => {
        setSelectedMovimiento(movimiento);
        setIsModalVisible(true);
    };

    // Template para mostrar la cantidad centrada con Badge
    const cantidadBodyTemplate = (rowData: any) => {
        return (
            <div style={{ textAlign: 'center' }}>
                <Badge value={rowData.total_cantidad} severity="info" />
            </div>
        );
    };

    // Template para mostrar el total de productos con Badge
    const productosBodyTemplate = (rowData: any) => {
        return (
            <div style={{ textAlign: 'center' }}>
                <Badge value={rowData.total_productos} severity="success" />
            </div>
        );
    };

    const exportToExcel = () => {
        const excelData = movimientosFiltrados.map(m => ({
            Folio: m.folio,
            Fecha: formatDate(m.fecha),
            Tipo: m.tipo.charAt(0).toUpperCase() + m.tipo.slice(1),
            'Total Productos': m.total_productos,
            'Total Cantidad': m.total_cantidad,
            Observaciones: m.observaciones,
            ...(m.tipo === 'entrada' && { Referencia: m.referencia }),
            ...(m.tipo === 'salida' && {
                'Sucursal Destino': m.sucursal_destino,
                'Departamento Destino': m.departamento_destino,
                Usuario: m.usuario
            }),
            ...(m.tipo === 'mantenimiento' && {
                Técnico: m.tecnico,
                'Producto Principal': m.producto_principal
            })
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Totales');
        const fileName = `totales_${tipoMovimiento}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const exportToPDF = () => {
        const doc = new jsPDF('l', 'pt', 'a4');
        const img = new Image();
        img.src = '/dist/img/bbtt2.png';
        
        img.onload = function () {
            // Agregar logo
            doc.addImage(img, 'PNG', 40, 20, 65, 65);

            // Configurar propiedades del documento
            doc.setProperties({
                title: 'Reporte de Totales',
                subject: `Totales de ${tipoMovimiento.charAt(0).toUpperCase() + tipoMovimiento.slice(1)}s`,
                author: 'Sistema de Inventarios',
                keywords: 'totales, reporte, pdf, inventario',
                creator: 'Sistema de Inventarios'
            });

            // Título principal
            doc.setFontSize(18);
            doc.setTextColor(40);
            const titulo = `Totales de ${tipoMovimiento.charAt(0).toUpperCase() + tipoMovimiento.slice(1)}s`;
            doc.text(titulo, 150, 40);
            
            // Fecha de generación
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);

            let y = 100;

            // Información de filtros aplicados
            doc.setFontSize(12);
            doc.setTextColor(40);
            
            if (fechaInicio || fechaFin) {
                doc.text(`Rango de fechas:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${fechaInicio || 'Sin fecha inicio'} - ${fechaFin || 'Sin fecha fin'}`, 150, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
            }

            if (sucursalSeleccionada && tipoMovimiento === 'salida') {
                doc.text(`Sucursal:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${sucursalSeleccionada.nombre}`, 100, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
            }

            doc.text(`Total de registros:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${movimientosFiltrados.length}`, 160, y);
            doc.setFont('helvetica', 'normal');
            y += 30;

            // Preparar columnas según el tipo de movimiento
            let columns: string[] = ['Folio', 'Fecha', 'Total Productos', 'Total Cantidad'];
            
            if (tipoMovimiento === 'entrada') {
                columns.push('Referencia', 'Observaciones');
            } else if (tipoMovimiento === 'salida') {
                columns.push('Sucursal Destino', 'Departamento Destino', 'Usuario', 'Observaciones');
            } else {
                columns.push('Técnico', 'Producto Principal', 'Observaciones');
            }

            // Preparar datos
            const rows = movimientosFiltrados.map(m => {
                let row = [
                    m.folio,
                    formatDate(m.fecha),
                    m.total_productos.toString(),
                    m.total_cantidad.toString()
                ];

                if (tipoMovimiento === 'entrada') {
                    row.push(m.referencia || '-', m.observaciones || '-');
                } else if (tipoMovimiento === 'salida') {
                    row.push(
                        m.sucursal_destino || '-',
                        m.departamento_destino || '-',
                        m.usuario || '-',
                        m.observaciones || '-'
                    );
                } else {
                    row.push(
                        m.tecnico || '-',
                        m.producto_principal || '-',
                        m.observaciones || '-'
                    );
                }

                return row;
            });

            // Generar tabla
            const autoTableResult = autoTable(doc, {
                head: [columns],
                body: rows,
                startY: y,
                margin: { horizontal: 40 },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    fontSize: 9
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 60, halign: 'center' }, // Folio
                    1: { cellWidth: 80, halign: 'center' }, // Fecha
                    2: { cellWidth: 80, halign: 'center' }, // Total Productos
                    3: { cellWidth: 80, halign: 'center' }, // Total Cantidad
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245],
                },
                didDrawPage: function (data: any) {
                    const pageCount = doc.getNumberOfPages();
                    const currentPage = data.pageNumber;
                    doc.setFontSize(10);
                    doc.setTextColor(150);
                    doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                }
            });

            // Resumen al final
            let finalY = (autoTableResult && autoTableResult.finalY) || (y + 200);
            finalY += 30;

            doc.setFontSize(12);
            doc.setTextColor(40);
            doc.text('Resumen:', 40, finalY);
            finalY += 20;

            const totalProductos = movimientosFiltrados.reduce((sum, m) => sum + m.total_productos, 0);
            const totalCantidad = movimientosFiltrados.reduce((sum, m) => sum + m.total_cantidad, 0);

            doc.setFontSize(10);
            doc.text(`Total de productos únicos: ${totalProductos}`, 40, finalY);
            finalY += 15;
            doc.text(`Total de cantidad: ${totalCantidad}`, 40, finalY);
            finalY += 15;
            doc.text(`Promedio de productos por movimiento: ${movimientosFiltrados.length > 0 ? (totalProductos / movimientosFiltrados.length).toFixed(2) : 0}`, 40, finalY);

            // Línea de firma
            finalY += 40;
            doc.setDrawColor(100);
            doc.line(300, finalY, 550, finalY);
            doc.text('Firma de conformidad', 425, finalY + 20, { align: 'center' });

            // Guardar archivo
            const fileName = `totales_${tipoMovimiento}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);

            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'PDF generado correctamente',
                life: 3000
            });
        };

        // Manejar error si no se puede cargar la imagen
        img.onerror = function () {
            console.warn('No se pudo cargar el logo, generando PDF sin imagen');
            
            // Generar PDF sin logo
            doc.setProperties({
                title: 'Reporte de Totales',
                subject: `Totales de ${tipoMovimiento.charAt(0).toUpperCase() + tipoMovimiento.slice(1)}s`,
                author: 'Sistema de Inventarios',
                keywords: 'totales, reporte, pdf, inventario',
                creator: 'Sistema de Inventarios'
            });

            doc.setFontSize(18);
            doc.setTextColor(40);
            const titulo = `Totales de ${tipoMovimiento.charAt(0).toUpperCase() + tipoMovimiento.slice(1)}s`;
            doc.text(titulo, 40, 40);

            // Continuar con el resto del PDF sin logo...
            const fileName = `totales_${tipoMovimiento}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);

            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'PDF generado correctamente',
                life: 3000
            });
        };
    };

    // Calcular estadísticas para el resumen
    const totalProductos = movimientosFiltrados.reduce((sum, m) => sum + m.total_productos, 0);
    const totalCantidad = movimientosFiltrados.reduce((sum, m) => sum + m.total_cantidad, 0);
    const promedioProductos = movimientosFiltrados.length > 0 ? (totalProductos / movimientosFiltrados.length).toFixed(1) : '0';

    return (
        <div>
            <Toast ref={toast} />
            <Navbar />
            <Sidebar />
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                {/* Header principal mejorado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{
                            margin: 0,
                            color: '#1f2937',
                            fontSize: '2rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            📊 Totales de Movimientos
                        </h1>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className="pi pi-chart-line"></i>
                            {loading ? 'CARGANDO...' : 'DATOS LISTOS'}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Panel de filtros mejorado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <h3 style={{
                        margin: '0 0 20px 0',
                        color: '#374151',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        🔍 Filtros de Búsqueda
                    </h3>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px',
                        marginBottom: '20px'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                📋 Tipo de Movimiento
                            </label>
                            <Dropdown
                                value={tipoMovimiento}
                                options={tiposMovimiento}
                                onChange={(e) => setTipoMovimiento(e.value)}
                                placeholder="Seleccionar tipo"
                                style={{
                                    width: '100%',
                                    height: '40px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>

                        {tipoMovimiento === 'salida' && (
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    marginBottom: '6px',
                                    color: '#374151'
                                }}>
                                    🏢 Sucursal
                                </label>
                                <Dropdown
                                    value={sucursalSeleccionada}
                                    options={sucursalesCatalogo}
                                    onChange={(e) => setSucursalSeleccionada(e.value)}
                                    optionLabel="nombre"
                                    placeholder="Todas las sucursales"
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                    showClear
                                />
                            </div>
                        )}

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                📅 Fecha Inicio
                            </label>
                            <InputText 
                                type="date" 
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '40px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                📅 Fecha Fin
                            </label>
                            <InputText 
                                type="date" 
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '40px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    </div>

                    {/* Botones de acción */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'center'
                    }}>
                        <Button
                            label="🔄 Actualizar"
                            onClick={buscarMovimientos}
                            loading={loading}
                            style={{
                                backgroundColor: '#3b82f6',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontWeight: '600'
                            }}
                        />
                        <Button
                            label="🗑️ Limpiar Filtros"
                            onClick={limpiarFiltros}
                            style={{
                                backgroundColor: '#6b7280',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontWeight: '600'
                            }}
                        />
                        <Button
                            label="📊 Excel"
                            onClick={exportToExcel}
                            disabled={movimientosFiltrados.length === 0}
                            style={{
                                backgroundColor: '#10b981',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontWeight: '600'
                            }}
                        />
                        <Button
                            label="📄 PDF"
                            onClick={exportToPDF}
                            disabled={movimientosFiltrados.length === 0}
                            style={{
                                backgroundColor: '#ef4444',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontWeight: '600'
                            }}
                        />
                    </div>
                </div>

                {/* Panel de estadísticas */}
                {movimientosFiltrados.length > 0 && (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <h3 style={{
                            margin: '0 0 16px 0',
                            color: '#374151',
                            fontSize: '1.2rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📈 Resumen Estadístico
                        </h3>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px'
                        }}>
                            <div style={{
                                backgroundColor: '#dbeafe',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '2px solid #3b82f6'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📋</div>
                                <div style={{ fontSize: '0.9rem', color: '#1e40af', fontWeight: '600', marginBottom: '4px' }}>MOVIMIENTOS</div>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#3b82f6' }}>{movimientosFiltrados.length}</div>
                            </div>

                            <div style={{
                                backgroundColor: '#dcfce7',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '2px solid #16a34a'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📦</div>
                                <div style={{ fontSize: '0.9rem', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>TOTAL PRODUCTOS</div>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#16a34a' }}>{totalProductos}</div>
                            </div>

                            <div style={{
                                backgroundColor: '#fef3c7',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '2px solid #f59e0b'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📊</div>
                                <div style={{ fontSize: '0.9rem', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>TOTAL CANTIDAD</div>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#f59e0b' }}>{totalCantidad}</div>
                            </div>

                            <div style={{
                                backgroundColor: '#fce7f3',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '2px solid #ec4899'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📈</div>
                                <div style={{ fontSize: '0.9rem', color: '#be185d', fontWeight: '600', marginBottom: '4px' }}>PROMEDIO</div>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#ec4899' }}>{promedioProductos}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabla de resultados mejorada */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            color: '#374151',
                            fontSize: '1.2rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📋 Totales de {tipoMovimiento === 'entrada' ? '📈 Entradas' : tipoMovimiento === 'salida' ? '📉 Salidas' : '🔧 Mantenimientos'}
                        </h3>
                        
                        <div style={{ position: 'relative' }}>
                            <i className="pi pi-search" style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '0.9rem'
                            }}></i>
                            <InputText
                                placeholder="Buscar en resultados..."
                                value={globalFilterValue}
                                onChange={onGlobalFilterChange}
                                style={{
                                    width: '300px',
                                    height: '40px',
                                    paddingLeft: '40px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb'
                    }}>
                        <DataTable
                            value={movimientosFiltrados}
                            globalFilter={globalFilterValue}
                            responsiveLayout="scroll"
                            stripedRows
                            size="small"
                            emptyMessage={`📭 No hay ${tipoMovimiento}s disponibles`}
                            style={{ fontSize: '0.875rem' }}
                            scrollable
                            scrollHeight="500px"
                            onRowClick={(e) => showMovimientoDetail(e.data as MovimientoTotal)}
                            globalFilterFields={['folio', 'observaciones', 'referencia', 'sucursal_destino', 'departamento_destino', 'usuario', 'tecnico']}
                            loading={loading}
                            rowHover
                        >
                            <Column 
                                field="folio" 
                                header="📄 Folio" 
                                style={{ width: '100px', fontWeight: 'bold', textAlign: 'center' }}
                                sortable
                            />
                            <Column
                                field="fecha"
                                header="📅 Fecha"
                                body={(rowData) => formatDate(rowData.fecha)}
                                style={{ width: '120px', textAlign: 'center' }}
                                sortable
                            />
                            <Column 
                                field="total_productos" 
                                header="📦 Total Productos" 
                                body={productosBodyTemplate}
                                style={{ width: '120px' }}
                                sortable
                            />
                            <Column 
                                field="total_cantidad" 
                                header="📊 Total Cantidad" 
                                body={cantidadBodyTemplate}
                                style={{ width: '120px' }}
                                sortable
                            />
                            {tipoMovimiento === 'entrada' && (
                                <Column 
                                    field="referencia" 
                                    header="🔗 Referencia" 
                                    style={{ width: '150px' }}
                                    sortable
                                />
                            )}
                            {tipoMovimiento === 'salida' && (
                                <>
                                    <Column 
                                        field="sucursal_destino" 
                                        header="🏢 Sucursal Destino" 
                                        style={{ width: '150px' }}
                                        sortable
                                    />
                                    <Column 
                                        field="departamento_destino" 
                                        header="🏬 Departamento Destino" 
                                        style={{ width: '150px' }}
                                        sortable
                                    />
                                    <Column 
                                        field="usuario" 
                                        header="👤 Usuario" 
                                        style={{ width: '120px' }}
                                        sortable
                                    />
                                </>
                            )}
                            {tipoMovimiento === 'mantenimiento' && (
                                <>
                                    <Column 
                                        field="tecnico" 
                                        header="👨‍🔧 Técnico" 
                                        style={{ width: '150px' }}
                                        sortable
                                    />
                                    <Column 
                                        field="producto_principal" 
                                        header="📦 Producto Principal" 
                                        style={{ width: '200px' }}
                                        sortable
                                    />
                                </>
                            )}
                            <Column 
                                field="observaciones" 
                                header="📝 Observaciones" 
                                style={{ minWidth: '200px' }}
                                sortable
                            />
                        </DataTable>
                    </div>
                </div>
            </div>

            {/* Modal de detalles mejorado */}
            <Dialog
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {tipoMovimiento === 'entrada' ? '📈 Detalles de Entrada' :
                         tipoMovimiento === 'salida' ? '📉 Detalles de Salida' :
                         '🔧 Detalles de Mantenimiento'} - {selectedMovimiento?.folio}
                    </div>
                }
                visible={isModalVisible}
                style={{ width: '90%' }}
                onHide={() => setIsModalVisible(false)}
            >
                {selectedMovimiento && (
                    <div style={{
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        padding: '20px'
                    }}>
                        <h4 style={{ 
                            margin: '0 0 20px 0', 
                            color: '#374151',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📋 Información General
                        </h4>
                        
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginBottom: '20px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600', width: '200px' }}>📄 Folio</th>
                                        <td style={{ padding: '12px', fontWeight: 'bold' }}>{selectedMovimiento.folio}</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📅 Fecha</th>
                                        <td style={{ padding: '12px' }}>{formatDate(selectedMovimiento.fecha)}</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📦 Total Productos</th>
                                        <td style={{ padding: '12px' }}>
                                            <Badge value={selectedMovimiento.total_productos} severity="success" />
                                        </td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📊 Total Cantidad</th>
                                        <td style={{ padding: '12px' }}>
                                            <Badge value={selectedMovimiento.total_cantidad} severity="info" />
                                        </td>
                                    </tr>
                                    {selectedMovimiento.tipo === 'entrada' && (
                                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🔗 Referencia</th>
                                            <td style={{ padding: '12px' }}>{selectedMovimiento.referencia}</td>
                                        </tr>
                                    )}
                                    {selectedMovimiento.tipo === 'salida' && (
                                        <>
                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🏢 Sucursal Destino</th>
                                                <td style={{ padding: '12px' }}>{selectedMovimiento.sucursal_destino}</td>
                                            </tr>
                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🏬 Departamento Destino</th>
                                                <td style={{ padding: '12px' }}>{selectedMovimiento.departamento_destino}</td>
                                            </tr>
                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>👤 Usuario</th>
                                                <td style={{ padding: '12px' }}>{selectedMovimiento.usuario}</td>
                                            </tr>
                                        </>
                                    )}
                                    {selectedMovimiento.tipo === 'mantenimiento' && (
                                        <>
                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>👨‍🔧 Técnico</th>
                                                <td style={{ padding: '12px' }}>{selectedMovimiento.tecnico}</td>
                                            </tr>
                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📦 Producto Principal</th>
                                                <td style={{ padding: '12px' }}>{selectedMovimiento.producto_principal}</td>
                                            </tr>
                                        </>
                                    )}
                                    <tr>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📝 Observaciones</th>
                                        <td style={{ padding: '12px' }}>{selectedMovimiento.observaciones}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h4 style={{ 
                            margin: '0 0 16px 0', 
                            color: '#000000ff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📋 Detalle de Productos
                        </h4>
                        
                        <div style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            overflowX: 'auto',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '0.875rem'
                            }}>
                                <thead style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    backgroundColor: '#374151',
                                    color: 'white'
                                }}>
                                    <tr>
                                        <th style={{ minWidth: '100px',color: '#000000ff', textAlign: 'center', padding: '12px' }}>🔑 Clave</th>
                                        <th style={{ minWidth: '200px', color: '#000000ff', textAlign: 'center', padding: '12px' }}>📝 Descripción</th>
                                        <th style={{ minWidth: '80px', color: '#000000ff', textAlign: 'center', padding: '12px' }}>📊 Cantidad</th>
                                        {selectedMovimiento.tipo === 'mantenimiento' && (
                                            <th style={{ minWidth: '150px', textAlign: 'center', padding: '12px' }}>📝 Observación</th>
                                        )}
                                        {selectedMovimiento.tipo === 'salida' && (
                                            <>
                                                <th style={{ minWidth: '120px', textAlign: 'center', padding: '12px' }}>🏢 Sucursal</th>
                                                <th style={{ minWidth: '130px', textAlign: 'center', padding: '12px' }}>🏬 Departamento</th>
                                                <th style={{ minWidth: '120px', textAlign: 'center', padding: '12px' }}>👤 Usuario</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedMovimiento.detalles && selectedMovimiento.detalles.length > 0 ? (
    selectedMovimiento.detalles.map((detalle: any, index: number) => (
                                            <tr key={index} style={{
                                                transition: 'background-color 0.2s',
                                                borderBottom: '1px solid #e5e7eb'
                                            }}>
                                                <td style={{
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle',
                                                    fontWeight: '500',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '12px'
                                                }}>
                                                    {detalle.cve_producto}
                                                </td>
                                                <td style={{
                                                    verticalAlign: 'middle',
                                                    paddingLeft: '12px',
                                                    wordWrap: 'break-word',
                                                    padding: '12px'
                                                }}>
                                                    {getDescripcionProducto(detalle.cve_producto)}
                                                </td>
                                                <td style={{
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle',
                                                    padding: '12px'
                                                }}>
                                                    <Badge value={detalle.cantidad} severity="info" />
                                                </td>
                                                {selectedMovimiento.tipo === 'mantenimiento' && (
                                                    <td style={{
                                                        verticalAlign: 'middle',
                                                        paddingLeft: '12px',
                                                        wordWrap: 'break-word',
                                                        fontStyle: detalle.observaciones ? 'normal' : 'italic',
                                                        color: detalle.observaciones ? 'inherit' : '#6c757d',
                                                        padding: '12px'
                                                    }}>
                                                        {detalle.observaciones || '-'}
                                                    </td>
                                                )}
                                                {selectedMovimiento.tipo === 'salida' && (
                                                    <>
                                                        <td style={{
                                                            textAlign: 'center',
                                                            verticalAlign: 'middle',
                                                            wordWrap: 'break-word',
                                                            fontSize: '0.8rem',
                                                            padding: '12px'
                                                        }}>
                                                            {getNombreSucursal(detalle.id_sucursal_destino) || '-'}
                                                        </td>
                                                        <td style={{
                                                            textAlign: 'center',
                                                            verticalAlign: 'middle',
                                                            wordWrap: 'break-word',
                                                            fontSize: '0.85rem',
                                                            padding: '12px'
                                                        }}>
                                                            {getNombreDepartamento(detalle.cve_departamento_destino) || '-'}
                                                        </td>
                                                        <td style={{
                                                            textAlign: 'center',
                                                            verticalAlign: 'middle',
                                                            wordWrap: 'break-word',
                                                            fontWeight: '500',
                                                            color: '#16a34a',
                                                            padding: '12px'
                                                        }}>
                                                            {detalle.nombre_usuario || '-'}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={selectedMovimiento.tipo === 'entrada' ? 3 : selectedMovimiento.tipo === 'salida' ? 6 : 4} style={{
                                                textAlign: 'center',
                                                padding: '20px',
                                                fontStyle: 'italic',
                                                color: '#6c757d',
                                                backgroundColor: '#f8f9fa'
                                            }}>
                                                📭 No hay detalles disponibles
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default Totales;